import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, Plus } from "lucide-react";
import { PasswordConfirm } from "@/components/PasswordConfirm";
import { LicenseSupportCard } from "./empresa/LicenseSupportCard";
import { EmpresasTable } from "./empresa/EmpresasTable";
import { EmpresaFormDialog } from "./empresa/EmpresaFormDialog";
import { useEmpresaForm } from "./empresa/useEmpresaForm";

// Cadastro de empresas (tenants). Fonte da verdade: `empresas` do AuthContext — após CRUD, refreshEmpresas().
export default function Empresa() {
  const { refreshEmpresas, startImpersonate, user, empresas: ctxEmpresas } = useAuth();
  const navigate = useNavigate();
  const items = ctxEmpresas || [];
  const form = useEmpresaForm(refreshEmpresas);
  const [pwdConfirm, setPwdConfirm] = useState(null);

  const isSuperAdmin = !user?._impersonating && (user?.role === "super_admin" || (user?.role === "admin" && !(user?.empresa_ids || []).length));

  // Iter 77: kill switch SA-only da Z-API por empresa. Backend descarta o campo se não-SA tentar.
  const toggleZapiAtivo = async (empresa) => {
    if (!isSuperAdmin) return;
    const novoValor = !(empresa.zapi_ativo_global !== false);
    try {
      await api.put(`/empresas/${empresa.id}`, { zapi_ativo_global: novoValor });
      toast.success(novoValor
        ? `Z-API ATIVADA para ${empresa.nome} — gatilhos automáticos habilitados`
        : `Z-API DESATIVADA para ${empresa.nome} — nenhuma mensagem será disparada`);
      await refreshEmpresas();
    } catch (err) {
      toast.error(`Falha ao alterar Z-API: ${err.response?.data?.detail || err.message}`);
    }
  };

  const acessarAmbiente = async (empresa) => {
    try {
      await startImpersonate(empresa.id);
      toast.success(`Modo Suporte ativado para "${empresa.nome}"`);
      navigate(`/${empresa.slug}/`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao iniciar Modo Suporte");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/empresas/${id}`);
      toast.success("Empresa excluída");
      await refreshEmpresas();
      window.dispatchEvent(new Event("empresa-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
  };
  const askDelete = (e) => setPwdConfirm({ action: () => del(e.id), message: `Digite sua senha para excluir a empresa "${e.nome}":` });

  return (
    <div className="space-y-6" data-testid="page-empresa">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-extrabold flex items-center gap-3">
            <Building2 className="w-9 h-9 text-orange-600"/>Empresas
          </h1>
          <p className="text-muted-foreground">Cadastre múltiplas empresas · cada empresa tem entregas isoladas</p>
        </div>
        <Button onClick={form.openNew} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-nova-empresa">
          <Plus className="w-4 h-4 mr-2"/>Nova Empresa
        </Button>
      </div>

      <LicenseSupportCard canLoad={isSuperAdmin} />

      <EmpresasTable items={items} isSuperAdmin={isSuperAdmin}
        onToggleZapi={toggleZapiAtivo} onAcessar={acessarAmbiente} onEdit={form.openEdit} onDelete={askDelete} />

      <EmpresaFormDialog form={form} />

      <PasswordConfirm open={!!pwdConfirm} onClose={() => setPwdConfirm(null)}
        onConfirmed={() => pwdConfirm?.action?.()} message={pwdConfirm?.message}/>
    </div>
  );
}
